// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Spinner, useToast } from '@loa/ui-components';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { StepProgressBar } from '../components/wizard/StepProgressBar';
import {
  COA_WIZARD_STEPS,
  CoAWizardProvider,
  useCoAWizard,
} from '../components/coaWizard/CoAWizardContext';
import { CoAPolicySelectionStep } from '../components/coaWizard/CoAPolicySelectionStep';
import { CoAEmailCraftingStep } from '../components/coaWizard/CoAEmailCraftingStep';
import { invitationApi } from '../api/invitationApi';
import { profileRequestApi } from '../api/profileRequestApi';
import { relationshipApi } from '../api/relationshipApi';
import { policyApi } from '../api/policyApi';
import { clientApi } from '../api/clientApi';
import type { Relationship } from '../types/relationship';
import type { Policy } from '../types/policy';
import type { Client } from '../types/client';

/**
 * Internal content of the CoA wizard. Lives inside {@link CoAWizardProvider}
 * so it can use the {@link useCoAWizard} hook for state and actions.
 */
function CoAWizardContent({
  relationship,
  policies,
  primaryClient,
}: Readonly<{
  relationship: Relationship;
  policies: Policy[];
  primaryClient: Client | null;
}>) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { setBreadcrumbItems, setBackTo, setBreadcrumbTitle } = usePageHeader();
  const {
    state,
    setSubmitting,
    setSubmitError,
  } = useCoAWizard();

  useEffect(() => {
    const relationshipLabel =
      relationship.relationshipName || relationship.profileName || 'Relationship';
    setBreadcrumbItems([
      { label: 'Relationships', to: '/relationships' },
      { label: relationshipLabel, to: `/relationships/${relationship.id}` },
      { label: 'Change Of Agency' },
    ]);
    setBackTo(`/relationships/${relationship.id}`);
    setBreadcrumbTitle(null);
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
  }, [
    relationship.id,
    relationship.relationshipName,
    relationship.profileName,
    setBreadcrumbItems,
    setBackTo,
    setBreadcrumbTitle,
  ]);

  const handleCancel = useCallback(() => {
    navigate(`/relationships/${relationship.id}`);
  }, [navigate, relationship.id]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      // Per-policy fan-out via the unified Profile Request endpoint.
      // CoaRequestHandler dedups idempotently on findActiveOnPolicy so
      // retries are safe.
      const requestIds: string[] = [];
      for (const policyId of state.selectedPolicyIds) {
        const created = await profileRequestApi.create({
          profileId: relationship.id,
          requestType: 'COA',
          policyId,
          initiatedByType: 'ADVISOR',
        });
        requestIds.push(created.id);
      }

      // Customised invitation flow — mirrors NewRequestPage's composer
      // step. The legacy CoA auto-email is suppressed by Profile Request
      // creation; the consolidated CLIENT_REQUESTS_INVITATION carries
      // the advisor's edited subject + body.
      if (primaryClient && requestIds.length > 0) {
        await invitationApi.sendRequestsInvitation(
          relationship.id,
          primaryClient.id,
          {
            requestIds,
            ...(state.isEmailModified && state.customSubject
              ? { customSubject: state.customSubject }
              : {}),
            ...(state.isEmailModified && state.customBody
              ? { customBody: state.customBody }
              : {}),
          },
        );
      }

      showToast(
        <>
          <span className="font-bold">Change of Agency</span> email has been sent.
        </>,
        'success',
      );
      navigate(`/relationships/${relationship.id}?tab=policies`);
    } catch (err: unknown) {
      let message: string | null | undefined = null;
      if (err && typeof err === 'object' && 'response' in err) {
        message = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      } else if (err && typeof err === 'object' && 'message' in err) {
        message = (err as { message?: string }).message;
      }
      setSubmitError(message ?? 'Failed to create the Change of Agency. Please try again.');
    }
  }, [
    relationship.id,
    state.selectedPolicyIds,
    state.isEmailModified,
    state.customSubject,
    state.customBody,
    primaryClient,
    setSubmitting,
    setSubmitError,
    navigate,
    showToast,
  ]);

  return (
    <>
      <div className="bg-white px-6 shadow-[0_4px_20px_0_rgba(0,0,0,0.04)]">
        <StepProgressBar steps={COA_WIZARD_STEPS} currentStep={state.currentStep} />
      </div>

      <div className="bg-page-bg flex-1">
        {state.currentStep === 1 && (
          <CoAPolicySelectionStep policies={policies} onCancel={handleCancel} />
        )}
        {state.currentStep === 2 && (
          <CoAEmailCraftingStep
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            policies={policies}
            primaryClient={primaryClient}
            relationship={relationship}
            profileId={relationship.id}
          />
        )}
      </div>
    </>
  );
}

/**
 * Route component for {@code /relationships/:id/change-of-agency}.
 *
 * <p>Loads the relationship to obtain the brokerage UUID needed by the
 * Phase 3 {@code POST /api/coa} endpoint, then renders the wizard inside
 * a {@link CoAWizardProvider}.
 */
export function CoAWizardPage() {
  const { id: profileId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [relationship, setRelationship] = useState<Relationship | null>(null);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [primaryClient, setPrimaryClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profileId) {
      setError('Missing relationship id');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const [rel, pols, clients] = await Promise.all([
          relationshipApi.getRelationship(profileId),
          policyApi.getPoliciesByProfile(profileId),
          clientApi.getClientsByProfile(profileId),
        ]);
        if (cancelled) return;
        setRelationship(rel);
        setPolicies(pols);
        setPrimaryClient(clients.find((c) => c.isPrimary) ?? clients[0] ?? null);
      } catch (err: unknown) {
        if (cancelled) return;
        const message =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message?: string }).message)
            : 'Failed to load the relationship';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const initialState = useMemo(() => ({}), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner />
      </div>
    );
  }

  if (error || !relationship) {
    return (
      <div className="p-6">
        <Alert variant="error" header="Could not load relationship">
          {error ?? 'Relationship not found'}
        </Alert>
        <button
          type="button"
          onClick={() => navigate('/relationships')}
          className="mt-4 h-[40px] px-6 border border-[#E1E1E1] text-[#1E1E1E] rounded-button text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Back to relationships
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      <CoAWizardProvider initialState={initialState}>
        <CoAWizardContent
          relationship={relationship}
          policies={policies}
          primaryClient={primaryClient}
        />
      </CoAWizardProvider>
    </div>
  );
}

export default CoAWizardPage;
